import { supabase } from '@/integrations/supabase/client'
import {
    DEMO_TOKEN_STORAGE_KEY,
    demoAccessFailure,
    errorStatus,
    isIssuedToken,
    isTokenCheck,
    type DemoAccessFailure,
} from '@/lib/demoAccess'

type Result<T> = { ok: true; value: T } | { ok: false; failure: DemoAccessFailure }

// A 2xx answer that does not pass `isExpected` is not trusted: the gate stays closed.
const callDemoAccess = async <T>(
    body: Record<string, string>,
    isExpected: (value: unknown) => value is T,
): Promise<Result<T>> => {
    try {
        const { data, error } = await supabase.functions.invoke('demo-access', { body })
        if (error) return { ok: false, failure: demoAccessFailure(errorStatus(error)) }
        if (!isExpected(data)) return { ok: false, failure: 'unavailable' }
        return { ok: true, value: data }
    } catch {
        return { ok: false, failure: 'unavailable' }
    }
}

export const requestDemoToken = (code: string) => callDemoAccess({ code }, isIssuedToken)

export const checkDemoToken = (token: string) => callDemoAccess({ token }, isTokenCheck)

// Without storage (blocked or private mode) the code is simply asked on every visit.
export const readStoredToken = (): string | null => {
    try {
        return localStorage.getItem(DEMO_TOKEN_STORAGE_KEY)
    } catch {
        return null
    }
}

export const storeToken = (token: string): void => {
    try {
        localStorage.setItem(DEMO_TOKEN_STORAGE_KEY, token)
    } catch {
        // Storage unavailable: the code is asked again on the next load.
    }
}

export const forgetStoredToken = (): void => {
    try {
        localStorage.removeItem(DEMO_TOKEN_STORAGE_KEY)
    } catch {
        // Storage unavailable: nothing was stored.
    }
}
